<template>
    <Primitive
        :as="as"
        :class="classes"
    >
        <div :class="element('inner')">
            <div
                v-if="$slots.logo || title"
                :class="element('header')"
            >
                <div
                    v-if="$slots.logo"
                    :class="element('logo')"
                >
                    <slot name="logo" />
                </div>

                <p
                    v-if="title"
                    :class="element('title')"
                >
                    {{ title }}
                </p>
            </div>

            <nav
                v-if="socialLinks.length"
                :class="element('social')"
                aria-label="Social media"
            >
                <ul :class="element('social-links')">
                    <li
                        v-for="link in socialLinks"
                        :key="`${link.platform}:${link.href}`"
                    >
                        <a
                            :class="element('social-link')"
                            :href="link.href"
                            :aria-label="link.label"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <slot
                                name="social-icon"
                                :platform="link.platform"
                                :label="link.label"
                            >
                                <span :class="element('social-label')">{{ link.label }}</span>
                            </slot>
                        </a>
                    </li>
                </ul>
            </nav>

            <nav
                v-if="columns.length"
                :class="element('nav')"
                aria-label="Footer"
            >
                <ul :class="element('columns')">
                    <li
                        v-for="column in columns"
                        :key="column.label"
                        :class="element('column')"
                    >
                        <component
                            :is="isColumnLink(column) ? linkAs : 'p'"
                            :class="headingClasses(column)"
                            v-bind="isColumnLink(column) ? linkAttrs(column as KFooterLink) : {}"
                        >
                            {{ column.label }}
                        </component>

                        <ul
                            v-if="column.items?.length"
                            :class="element('links')"
                        >
                            <li
                                v-for="link in column.items"
                                :key="link.label"
                            >
                                <component
                                    :is="linkAs"
                                    :class="element('link')"
                                    v-bind="linkAttrs(link)"
                                >
                                    {{ link.label }}
                                </component>
                            </li>
                        </ul>
                    </li>
                </ul>
            </nav>
        </div>

        <div
            v-if="$slots.bottom || copyright || credits"
            :class="element('bottom')"
        >
            <div :class="element('bottom-inner')">
                <slot name="bottom">
                    <p
                        v-if="copyright"
                        :class="element('copyright')"
                    >
                        {{ copyright }}
                    </p>

                    <p
                        v-if="credits"
                        :class="element('credits')"
                    >
                        {{ credits }}
                    </p>
                </slot>
            </div>
        </div>
    </Primitive>
</template>

<script setup lang="ts">
import type { KFooterColumn, KFooterLink, KFooterProps } from './types'
import useBem from '@yeeroen/use-bem'
import { Primitive } from 'reka-ui'

import { computed } from 'vue'

defineOptions({ name: 'KFooter' })

withDefaults(defineProps<KFooterProps>(), {
    as: 'footer',
    columns: () => [],
    socialLinks: () => [],
    linkAs: 'a',
})

defineSlots<{
    /** Brand mark. Owns its own link, image and alt text. */
    'logo'?: () => unknown
    /** Visual for a social platform. The link itself supplies its accessible name. */
    'social-icon'?: (props: { platform: string, label: string }) => unknown
    /** Replaces the default copyright and credits row. */
    'bottom'?: () => unknown
}>()

const { block, element, modifier } = useBem('k-footer')

const classes = computed(() => block())

/**
 * A childless column with an href renders its heading as the link itself, so
 * the column's destination is never lost behind a dead label.
 */
function isColumnLink(column: KFooterColumn) {
    return !column.items?.length && Boolean(column.href)
}

function headingClasses(column: KFooterColumn) {
    return element('heading', modifier('link', isColumnLink(column)))
}

function linkAttrs(link: KFooterLink) {
    return {
        href: link.href,
        target: link.external ? '_blank' : undefined,
        rel: link.external ? 'noopener noreferrer' : undefined,
    }
}
</script>

<style lang="scss">
@use './k-footer.scss';
</style>
